import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CardPaymentFlow from '@/components/CardPaymentFlow';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

/**
 * Payment Demo Page
 * 
 * For testing Nexi SoftPOS integration
 * NOT for production use
 */
const PaymentDemo = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showPayment, setShowPayment] = useState(false);
  const [amount, setAmount] = useState('25.50');
  const [orderId] = useState(`DEMO_${Date.now()}`);

  const handlePaymentSuccess = (paymentData) => {
    console.log('Payment Success:', paymentData);
    toast.success(t('payment.success'), {
      description: `${t('payment.transactionId')}: ${paymentData.transactionId}`
    });
    
    // In production: Send to cash-x backend
    setTimeout(() => {
      setShowPayment(false);
      toast.success(t('payment.deliveryCompleted'));
    }, 2000);
  };

  const handlePaymentCancel = () => {
    console.log('Payment Cancelled');
    toast.info(t('payment.cancelled'));
    setShowPayment(false);
  };

  const handlePaymentError = (error) => {
    console.error('Payment Error:', error);
    toast.error(t('payment.failed'), {
      description: error.message || t('payment.errorOccurred')
    });
  };

  if (showPayment) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-zinc-900 to-zinc-800">
        <CardPaymentFlow
          amount={parseFloat(amount)}
          currency="EUR"
          orderId={orderId}
          onSuccess={handlePaymentSuccess}
          onCancel={handlePaymentCancel}
          onError={handlePaymentError}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-zinc-900 to-zinc-800">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center gap-2 mb-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <CardTitle className="text-2xl">{t('payment.demo.title')}</CardTitle>
          </div>
          <p className="text-sm text-muted-foreground">
            {t('payment.demo.description')}
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Amount Input */}
          <div className="space-y-2">
            <Label htmlFor="amount">{t('payment.amount')}</Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="25.50"
            />
          </div>

          {/* Order Info */}
          <div className="space-y-2">
            <Label>{t('payment.orderId')}</Label>
            <Input
              value={orderId}
              readOnly
              disabled
            />
          </div>

          {/* Info Box */}
          <Card className="bg-blue-500/10 border-blue-500">
            <CardContent className="p-4">
              <p className="text-sm">
                <strong>{t('payment.note')}:</strong> {t('payment.demo.note')}
                <br /><br />
                <strong>{t('payment.requirements')}:</strong>
                <ul className="list-disc ml-4 mt-2 space-y-1">
                  <li>{t('payment.req.nfc')}</li>
                  <li>{t('payment.req.app')}</li>
                  <li>{t('payment.req.internet')}</li>
                </ul>
              </p>
            </CardContent>
          </Card>

          {/* Start Button */}
          <Button
            onClick={() => setShowPayment(true)}
            className="w-full h-12 text-lg"
            size="lg"
          >
            {t('payment.start')}
          </Button>

          {/* Back Button */}
          <Button
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="w-full"
          >
            {t('payment.backToDashboard')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentDemo;
